// src/CqcInspectorView.js
// Inspector view — a single place to see compliance progress across every
// client organisation and every site, read-only. Gated to platform admins
// both here (client-side, same convention as SiteManagement.js checking for
// the "Pharmace all" organisation) and server-side (requirePlatformAdmin in
// cqc.js) — the client-side check is a UX convenience only, never the real
// security boundary.
//
// Three-pane layout mirrors SiteManagement.js: organisations on the left,
// that organisation's sites in the middle, the selected site's full
// compliance pack on the right.

import React, { useEffect, useMemo, useState } from "react";
import { cqcApi } from "./cqcApi";
import { GROUPS, deriveSector, findItem } from "./cqcTaxonomy";
import { pctBg, fmtPct, RatingBadge } from "./cqcColors";
import { attestationIndex, applicabilityIndex, overallPctForSite, groupPctForSite } from "./cqcRollup";

const S = {
  wrap: { fontFamily: "system-ui, sans-serif", display: "flex", flexDirection: "column",
          height: "calc(100vh - 130px)", minHeight: 480 },
  header: { display: "flex", alignItems: "center", gap: 10, padding: "0 0 10px",
            borderBottom: "1px solid #e5e7eb", flexShrink: 0 },
  h1: { fontSize: 20, fontWeight: 700, margin: 0 },
  sub: { fontSize: 12, color: "#6b7280" },
  body: { flex: 1, display: "grid", gridTemplateColumns: "260px 260px 1fr", gap: 12,
          paddingTop: 12, overflow: "hidden", minHeight: 0 },
  panel: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
           display: "flex", flexDirection: "column", overflow: "hidden" },
  panelHead: { padding: "10px 12px", borderBottom: "1px solid #e5e7eb", fontSize: 13, fontWeight: 700,
               color: "#374151", background: "#f9fafb", flexShrink: 0 },
  panelBody: { flex: 1, overflow: "auto" },
  orgRow: (active) => ({ padding: "9px 12px", fontSize: 13, cursor: "pointer",
                          borderBottom: "1px solid #f1f5f9", background: active ? "#eff6ff" : "transparent",
                          fontWeight: active ? 700 : 500 }),
  siteRow: (active) => ({ padding: "9px 12px", fontSize: 13, cursor: "pointer",
                           borderBottom: "1px solid #f1f5f9", background: active ? "#eff6ff" : "transparent",
                           fontWeight: active ? 700 : 500 }),
  detailPad: { padding: 16 },
  kqRow: { display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid #f1f5f9" },
  kqLabel: { fontSize: 13, fontWeight: 600, flex: 1 },
  pctChip: (pct) => ({ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: pctBg(pct) }),
  empty: { fontSize: 13, color: "#9ca3af", padding: 30, textAlign: "center" },
};

export default function CqcInspectorView({ isPlatformAdmin }) {
  const [orgs, setOrgs] = useState([]);
  const [activeOrg, setActiveOrg] = useState(null);
  const [orgSites, setOrgSites] = useState([]);
  const [activeSiteId, setActiveSiteId] = useState(null);
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isPlatformAdmin === false) return;
    cqcApi.inspectorOrganisations().then((d) => setOrgs(d.organisations || [])).catch((e) => setError(e.message));
  }, [isPlatformAdmin]);

  useEffect(() => {
    if (!activeOrg) { setOrgSites([]); return; }
    cqcApi.inspectorSites(activeOrg).then((d) => setOrgSites(d.sites || [])).catch((e) => setError(e.message));
  }, [activeOrg]);

  useEffect(() => {
    if (!activeSiteId) { setPack(null); return; }
    cqcApi.inspectorSitePack(activeSiteId).then(setPack).catch((e) => setError(e.message));
  }, [activeSiteId]);

  const attByKey = useMemo(() => (pack ? attestationIndex(pack.attestations) : {}), [pack]);
  const appByKey = useMemo(() => (pack ? applicabilityIndex(pack.applicability) : {}), [pack]);
  const naRecords = useMemo(
    () => (pack ? (pack.applicability || []).filter((r) => r.applicable === false) : []),
    [pack]
  );

  if (isPlatformAdmin === false) {
    return <div style={S.wrap}><div style={S.empty}>Inspector view is restricted to platform administrators.</div></div>;
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>CQC Inspector view</h1>
          <div style={S.sub}>Every client organisation's sites and compliance progress, in one place.</div>
        </div>
      </div>
      {error && <div style={{ color: "#991b1b", fontSize: 12.5, padding: "8px 0" }}>{error}</div>}
      <div style={S.body}>
        <div style={S.panel}>
          <div style={S.panelHead}>Organisations ({orgs.length})</div>
          <div style={S.panelBody}>
            {orgs.map((o) => (
              <div key={o.organisationName} style={S.orgRow(o.organisationName === activeOrg)}
                   onClick={() => { setActiveOrg(o.organisationName); setActiveSiteId(null); setPack(null); }}>
                {o.organisationName} <span style={{ color: "#9ca3af" }}>· {o.siteCount}</span>
              </div>
            ))}
            {orgs.length === 0 && <div style={S.empty}>No organisations have connected sites yet.</div>}
          </div>
        </div>

        <div style={S.panel}>
          <div style={S.panelHead}>{activeOrg ? `Sites — ${activeOrg}` : "Sites"}</div>
          <div style={S.panelBody}>
            {orgSites.map((s) => (
              <div key={s._id} style={S.siteRow(s._id === activeSiteId)} onClick={() => setActiveSiteId(s._id)}>
                {s.name}
                <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>{deriveSector(s).label}</div>
              </div>
            ))}
            {activeOrg && orgSites.length === 0 && <div style={S.empty}>No sites for this organisation.</div>}
          </div>
        </div>

        <div style={S.panel}>
          <div style={S.panelHead}>{pack ? pack.site.name : "Select a site"}</div>
          <div style={{ ...S.panelBody, ...S.detailPad }}>
            {!pack && <div style={S.empty}>Pick an organisation, then a site, to view its compliance pack.</div>}
            {pack && (
              <>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
                  <RatingBadge rating={pack.site.ratings && pack.site.ratings.overall} />
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    {pack.site.postcode} · CQC ID {pack.site.cqcLocationId} · {deriveSector(pack.site).label}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
                  {(pack.site.gacServiceTypes || []).join(", ") || "No service types recorded"}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                  Overall: {fmtPct(overallPctForSite(attByKey, pack.site._id, GROUPS, appByKey))} evidenced
                </div>
                {GROUPS.map((g) => (
                  <div key={g.id} style={S.kqRow}>
                    <div style={S.kqLabel}>{g.label}</div>
                    <div style={S.pctChip(groupPctForSite(attByKey, pack.site._id, g, appByKey))}>
                      {fmtPct(groupPctForSite(attByKey, pack.site._id, g, appByKey))}
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 14 }}>
                  {pack.attestations.length} evidence source{pack.attestations.length === 1 ? "" : "s"} recorded
                  {pack.entries.length ? ` · ${pack.entries.length} manual entries logged` : ""}.
                </div>
                {naRecords.length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#475569", marginBottom: 6 }}>
                      Marked not applicable at this site ({naRecords.length})
                    </div>
                    {naRecords.map((r) => {
                      const item = findItem(r.groupId, r.itemId);
                      return (
                        <div key={`${r.groupId}|${r.itemId}`} style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                          <strong>{item ? item.label : r.itemId}</strong> — {r.reason}
                          {r.recordedBy && (
                            <span style={{ color: "#94a3b8" }}>
                              {" "}(set by {r.recordedBy}{r.recordedAt ? ` on ${new Date(r.recordedAt).toLocaleDateString()}` : ""})
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
